import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";

import { Injectable } from "@angular/core";
import { HttpClient, HttpBackend, HttpHeaders, HttpEventType } from "@angular/common/http";
import { LoginService } from "src/app/login/login.service";
import { Subject } from "rxjs";
import { map } from "rxjs";

@Injectable({ providedIn: "root" })
export class FilesManagerService {
  public fileChanged: Subject<any> = new Subject();
  private httpClient: HttpClient;

  public constructor(
    handler: HttpBackend,
    public _http: HttpClient,
    public loginService: LoginService
  ) {
    this.httpClient = new HttpClient(handler);
  }

  fileSizeUnit: number = 1024;

  addDefaultFolders(conversation_id: Number) {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/add-default-folders`;
    return this._http.post(url, conversation_id);
  }

  getNavigatedFile(conversation_id: any, etag: string) {
    return this._http.get(
      `${environment.apiUrl}/file-manager/${conversation_id}/fetch/${etag}`
    );
  }

  getRecentFiles(conversation_id): Observable<any> {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/latest`;
    return this._http.get(url);
  }

  getPagedFiles(
    listFiltering: string,
    conversation_id: number,
    rows: number | undefined = undefined,
    page: number | undefined = undefined,
    orderBy: string | undefined = undefined,
    direction: string | undefined = undefined
  ): Observable<any> {
    let url = `${environment.apiUrl}/file-manager/${conversation_id}/list?paged=true`;
    listFiltering ? url += `&filtering=${listFiltering}` : null;
    page ? url += `&page=${page}` : null;
    rows ? url += `&rows=${rows}` : null;
    orderBy ? url += `&order_by=${orderBy}` : null;
    direction ? url += `&direction=${direction}` : null;

    return this._http.get(url);
  }

  getEasyUploadFiles(conversation_id, orderBy=undefined): Promise<any> {
    let url = `${environment.apiUrl}/file-manager/${conversation_id}/navigate/easy-upload`;
    if (orderBy) url += `?order-by=${orderBy}`;
    return this._http.get(url).toPromise();
  }

  getMessagesFiles(conversation_id, orderBy=undefined): Promise<any> {
    let url = `${environment.apiUrl}/file-manager/${conversation_id}/navigate/messages`;
    if (orderBy) url += `?order-by=${orderBy}`;
    return this._http.get(url).toPromise();
  }

  getTrackedItemsFiles(conversation_id, orderBy=undefined): Promise<any> {
    let url = `${environment.apiUrl}/file-manager/${conversation_id}/navigate/tracked-items`;
    if (orderBy) url += `?order-by=${orderBy}`;
    return this._http.get(url).toPromise();
  }

  getAll(conversation_id, orderBy=undefined): Promise<any> {
    let url = `${environment.apiUrl}/file-manager/${conversation_id}/all`;
    if (orderBy) url += `?order-by=${orderBy}`;

    return this._http.get(url).toPromise();
  }

  getDefaultAll(company_id): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${company_id}/all-default`;
    return this._http.get(url).toPromise();
  }

  getFolder(conversation_id, parent_folder_uuid, orderBy=undefined): Promise<any> {
    let url = `${environment.apiUrl}/file-manager/${conversation_id}/navigate/${parent_folder_uuid}`;
    if (orderBy) url += `?order-by=${orderBy}`;
    return this._http.get(url).toPromise();
  }

  getDefaultFolder(company_id, parent_folder_uuid): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${company_id}/navigate/default/${parent_folder_uuid}`;
    return this._http.get(url).toPromise();
  }

  getFileDetails(conversation_id, etag): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/get/${etag}`;
    return this._http.get(url).toPromise();
  }

  getFileDetailsByUuid(uuid): Observable<any> {
    const url = `${environment.apiUrl}/file-manager/file/${uuid}`;
    return this._http.get(url);
  }

  getParentFolder(conversation_id, parent_folder_uuid): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/navigate/${parent_folder_uuid}/parent`;
    return this._http.get(url).toPromise();
  }

  getParentDefaultFolder(company_id, parent_folder_uuid): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${company_id}/navigate/default/${parent_folder_uuid}/parent`;
    return this._http.get(url).toPromise();
  }

  getStaffOnly(conversation_id, orderBy=undefined): Promise<any> {
    let url = `${environment.apiUrl}/file-manager/${conversation_id}/navigate/staff-only`;
    if (orderBy) url += `?order-by=${orderBy}`;
    return this._http.get(url).toPromise();
  }

  getStaffDefaultOnly(company_id): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${company_id}/navigate/staff-default-only`;
    return this._http.get(url).toPromise();
  }

  getDeleted(conversation_id): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/navigate/trash`;
    return this._http.get(url).toPromise();
  }

  getSecuredFileUrl(file_uuid): string {
    return `${environment.apiUrl}/file-manager/file/${file_uuid}`;
  }

  getFileDownload(url): Observable<any> {
    return this.httpClient.get(url, { responseType: "blob" });
  }

  getFileCount(conversation_id, folder) {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/count/all/${folder}`;
    return this._http.get(url).toPromise();
  }

  getSignRequestCount(conversation_id: number): Promise<{ count: number }> {
    const url = `${environment.apiUrl}/file-manager/e-sign/${conversation_id}/count`;
    return this._http.get<{ count: number }>(url).toPromise();
  }

  deleteFile(conversation_id, uuid): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/file/action/delete/${uuid}`;
    return this._http.delete(url).toPromise();
  }

  deleteSelectedFiles(
    conversation_id: number,
    uuids_array: string[]
  ): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/file/action/delete_selected/`;
    return this._http.post(url, uuids_array).toPromise();
  }

  deleteFolder(conversation_id, uuid): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/folder/action/delete/${uuid}`;
    return this._http.delete(url).toPromise();
  }

  deleteDefaultFolder(company_id, uuid): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${company_id}/folder/action/delete-default/${uuid}`;
    return this._http.delete(url).toPromise();
  }

  renameFile(conversation_id, uuid: string, name: string): Promise<any> {
    const data = {
      uuid: uuid,
      name: name,
    };

    const url = `${environment.apiUrl}/file-manager/${conversation_id}/file/action/rename`;
    return this._http.post(url, data).toPromise();
  }

  renameFolder(conversation_id, uuid: string, name: string): Promise<any> {
    const data = {
      uuid: uuid,
      name: name,
    };

    const url = `${environment.apiUrl}/file-manager/${conversation_id}/folder/action/rename`;
    return this._http.post(url, data).toPromise();
  }

  renameDefaultFolder(company_id, uuid: string, name: string): Promise<any> {
    const data = {
      uuid: uuid,
      name: name,
    };

    const url = `${environment.apiUrl}/file-manager/${company_id}/folder/action/rename-default`;
    return this._http.post(url, data).toPromise();
  }

  createFolder(
    conversation_id,
    parentUUID: string,
    name: string,
    staffOnly: boolean
  ): Promise<any> {
    const data = {
      parent_uuid: parentUUID,
      name: name,
      staff_only: staffOnly,
      shared: !staffOnly,
    };

    const url = `${environment.apiUrl}/file-manager/${conversation_id}/folder/action/new`;
    return this._http.post(url, data).toPromise();
  }

  createDefaultFolder(
    company_id,
    parentUUID: string,
    name: string,
    isStaffOnly: boolean,
    addFolderAutomatically: boolean
  ): Promise<any> {
    const data = {
      parent_uuid: parentUUID,
      name: name,
      staff_only: isStaffOnly,
      shared: !isStaffOnly,
      add_folder_automatically: addFolderAutomatically
    };

    const url = `${environment.apiUrl}/file-manager/${company_id}/folder/action/new-default`;
    return this._http.post(url, data).toPromise();
  }

  moveFile(conversation_id: any, payload: any): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/file/action/move`;
    return this._http.post(url, payload).toPromise();
  }

  moveFiles(
    conversation_id: any,
    file_etag_list: any[],
    folder_uuid: string
  ): Promise<any> {
    const data = {
      file_list: file_etag_list,
      folder: folder_uuid,
    };

    const url = `${environment.apiUrl}/file-manager/${conversation_id}/file/action/move`;
    return this._http.post(url, data).toPromise();
  }

  moveFolder(
    conversation_id,
    folder_to_move: string[],
    folder_uuid: string
  ): Promise<any> {
    const data = {
      folder_to_move: folder_to_move,
      folder: folder_uuid,
    };

    const url = `${environment.apiUrl}/file-manager/${conversation_id}/folder/action/move`;
    return this._http.post(url, data).toPromise();
  }

  copyFilesAndFolders(
    conversation_id: number,
    files_to_copy: string[],
    folders_to_copy: string[],
    folder_uuid: string
  ): Promise<any> {
    const data = {
      folders_to_copy: folders_to_copy,
      files_to_copy: files_to_copy,
      folder: folder_uuid,
    };

    const url = `${environment.apiUrl}/file-manager/${conversation_id}/archives/action/copy`;
    return this._http.post(url, data).toPromise();
  }

  uploadFiles(
    conversation_id: number,
    target: string,
    isStaff: boolean,
    formData: FormData
  ): Promise<any> {
    let visibility = "restricted";

    if (target) {
      formData.append("target_folder", target);
    }
    if (!isStaff) {
      visibility = "shared";
    }

    const url = `${environment.apiUrl}/file-manager/${conversation_id}/file/action/upload/${visibility}`;
    return this._http.post(url, formData).toPromise();
  }

  uploadFilesGeneric(formData: FormData): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/`;
    return this._http.post(url, formData).toPromise();
  }

  download(etag: string) {
    this.getFileDetailsByUuid(etag).subscribe((result) => {
      const data = result;
      const file = data["file"];
      const url = data["file"].url;

      this.getFileDownload(url).subscribe((sub) => {
        const blob = sub.slice(0, sub.size, data["file"].mime_type);
        const _url = window.URL.createObjectURL(blob);

        var link = document.createElement("a");
        link.href = _url;
        link.download = file.filename;

        link.dispatchEvent(
          new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );

        setTimeout(function () {
          // For Firefox it is necessary to delay revoking the ObjectURL
          window.URL.revokeObjectURL(_url);
          link.remove();
        }, 100);
      });
    });
  }

  zipAndDownloadFolderFiles(folderUuid): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/file/zip-and-download`;
    return this._http
      .post(url, { folderUuid }, { responseType: "blob" })
      .toPromise();
  }

  downloadFoldersContent(conversation_id: number) {
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/download-folders-content`;
    return this._http.get(url, { responseType: "blob" }).toPromise();
  }

  getFolderNameByUuid(folderUuid: number): Promise<any> {
    const url = `${environment.apiUrl}/file-manager/folder/action/folder-name/${folderUuid}`;
    return this._http.get(url).toPromise();
  }

  getSignatureRequests(conversation_id: number) {
    return this._http.get(
      `${environment.apiUrl}/file-manager/e-sign/${conversation_id}`
    );
  }

  createSignatureRequest(conversation_id: number, formData: FormData) {
    return this._http
      .post<{ embedded_url: string }>(
        `${environment.apiUrl}/file-manager/e-sign/${conversation_id}`,
        formData
      )
      .toPromise();
  }

  getSignatureRequestDetails(conversation_id: number, document_id: string) {
    return this._http
      .get(
        `${environment.apiUrl}/file-manager/e-sign/${conversation_id}/details/${document_id}`
      )
      .toPromise();
  }

  sendSignatureRequestReminder(conversation_id: number, document_id: string) {
    return this._http.post(
      `${environment.apiUrl}/file-manager/e-sign/${conversation_id}/details/${document_id}`,
      {}
    );
  }

  newUploadFiles(
    conversation_id: number,
    target: string,
    isStaff: boolean,
    formData: FormData
  ): Observable<any> {
    let visibility = "restricted";

    if (target) {
      formData.append("target_folder", target);
    }
    if (!isStaff) {
      visibility = "shared";
    }
    const headers = new HttpHeaders();
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/file/action/upload/${visibility}`;
    return this._http
      .post(url, formData, {
        headers,
        reportProgress: true,
        observe: "events",
      })
      .pipe(
        map((event) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const progress = Math.round((100 * event.loaded) / event.total);
              return { status: "progress", message: progress };

            case HttpEventType.Response:
              return { status: "completed" };
          }
        })
      );
  }

  uploadSingleFile(conversation_id: number, formData: FormData): Observable<any> {
    const headers = new HttpHeaders();
    const url = `${environment.apiUrl}/file-manager/${conversation_id}/file/action/upload`;
    return this._http
      .post(url, formData, {headers, reportProgress: true, observe: "events"})
      .pipe(
        map((event) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const progress = Math.round((100 * event.loaded) / event.total);
              return { status: "progress", message: progress };

            case HttpEventType.Response:
              return { status: "completed" };
          }
        })
      );
  }

  // getFileSize(fileSize: number): number {
  //   if (fileSize > 0) {
  //     if (fileSize < this.fileSizeUnit * this.fileSizeUnit) {
  //       fileSize = parseFloat((fileSize / this.fileSizeUnit).toFixed(2));
  //     } else if (
  //       fileSize <
  //       this.fileSizeUnit * this.fileSizeUnit * this.fileSizeUnit
  //     ) {
  //       fileSize = parseFloat(
  //         (fileSize / this.fileSizeUnit / this.fileSizeUnit).toFixed(2)
  //       );
  //     }
  //   }

  //   return fileSize;
  // }

  getFilesSizes(fileSizes: number[]): number {
    let totalSize = 0;

    for (const fileSize of fileSizes) {
      if (fileSize > 0) {
        if (fileSize < this.fileSizeUnit * this.fileSizeUnit) {
          totalSize += parseFloat((fileSize / this.fileSizeUnit).toFixed(2));
        } else if (
          fileSize <
          this.fileSizeUnit * this.fileSizeUnit * this.fileSizeUnit
        ) {
          totalSize += parseFloat(
            (fileSize / this.fileSizeUnit / this.fileSizeUnit).toFixed(2)
          );
        } else {
          totalSize += parseFloat(
            (
              fileSize /
              this.fileSizeUnit /
              this.fileSizeUnit /
              this.fileSizeUnit
            ).toFixed(2)
          );
        }
      }
    }

    return totalSize;
  }
}
